import React, { useEffect, useRef, useState } from 'react';
import { Container, Carousel, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSpring, animated } from '@react-spring/web'; // Import react-spring
import '../css/Home.css';
import '../css/Insurance.css';
import Review from '../components/review';

const Home = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [imageSrc, setImageSrc] = useState('img_clean_world_office.jpg'); // Default image
  const sectionRef = useRef(null);

  const [isInView, setIsInView] = useState(false);
  const [isInViewWhyChoose, setIsInViewWhyChoose] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Stop observing once visible
        }
      },
      {
        threshold: 0.4 // Trigger when 40% of the section is visible
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');

    const updateImageBasedOnScreenState = (e) => {
      if (e.matches) {
        setImageSrc('img_clean_world_office.jpg'); // Replace with your dimmed image path
      } else {
        setImageSrc('img_clean_world_office.jpg');
      }
    };

    // Set initial image based on the current state
    updateImageBasedOnScreenState(mediaQuery);

    // Add event listener for changes
    mediaQuery.addEventListener('change', updateImageBasedOnScreenState);

    // Cleanup on component unmount
    return () => {
      mediaQuery.removeEventListener('change', updateImageBasedOnScreenState);
    };
  }, []);

  const handleButtonClick = () => {
    navigate('/Refills'); // Adjust the path according to your routing setup
  };

  const handleFindOutMore = () => {
    navigate('/services/medical-waste'); // Adjust the path based on your routing setup
  };
  
  const handleNimbleRefill = () => {
    navigate('/services/compliant-hazardous-waste'); // Navigate to the new compliant hazardous waste page
  };

  const handleOTC = () => {
    navigate('/services/chemical-waste'); // Adjust the path based on your routing setup
  };

  const handleThanksGivingFindOutMore = () => {
    navigate('/Thanksgiving'); // Adjust the path based on your routing setup
  };

  const handleChristmasFindOutMore = () => {
    navigate('/Christmas'); // Adjust the path based on your routing setup
  };

  // Scroll handler for detecting when the logo section is in view
  const handleScroll = () => {
    const section = document.getElementById('insurance-section');
    if (section !== null) {
      const rect = section.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom >= 0;

      if (isVisible) {
        setIsInView(true);
      }
      else {
        setIsInView(false);
      }
    }
  };

  // Scroll handler for detecting when the "Why Choose Us?" section is in view
  const handleScrollWhyChoose = () => {
    const section = document.getElementById('why-choose-us');
    if (section !== null) {
      const rect = section.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom >= 0;
      if (isVisible) {
        setIsInViewWhyChoose(true);
      }
      else {
        setIsInViewWhyChoose(false);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleScrollWhyChoose);
    return () => {
      // window.removeEventListener('scroll', handleScroll);
      // window.removeEventListener('scroll', handleScrollWhyChoose);
    };
  }, []);

  // Slide-in animation for the "Why Choose Us?" section
  const slideInWhyChoose = useSpring({
    transform: isInViewWhyChoose ? 'translateX(0)' : 'translateX(-100%)',
    opacity: isInViewWhyChoose ? 1 : 0,
    config: { tension: 120, friction: 30 },
  });

  // Animation for the heading when the section comes into view
  const slideIn = useSpring({
    transform: isInView ? 'translateX(0)' : 'translateX(-100%)',
    opacity: isInView ? 1 : 0,
    // config: { tension: 250, friction: 25 }
    config: { tension: 120, friction: 30 }
  });

  const logosAnimation = useSpring({
    opacity: isInView ? 1 : 0,
    transform: isInView ? 'translateY(0)' : 'translateY(20px)',
    //config: { tension: 250, friction: 25 }
    config: { tension: 120, friction: 30 }
  });

  return (
    <Container style={{ marginTop: '0px' }} className="my-5">
      <Carousel interval={2000}>
        <Carousel.Item>
          <img className="d-block w-100" src="/industrial_incinerator.jpeg" alt="Industrial Incinerator" />
          <Carousel.Caption className="carousel-caption">
            <h2>Industrial Incineration</h2>
            <p>High-temperature incineration for complete destruction of hazardous and industrial wastes.</p>
          </Carousel.Caption>
        </Carousel.Item>
        <Carousel.Item>
          <img className="d-block w-100" src="img_2.jpg" alt="Second slide" />
          <Carousel.Caption className="carousel-caption">
            <h2>Compliant Hazardous Waste Solutions</h2>
            <p>Secure medical and hazardous waste management with advanced incineration and proven safety protocols.</p>
            <Button onClick={handleNimbleRefill} variant="primary" type="button" style={{ display: 'flex', alignItems: 'flex-end', flexDirection: 'column', fontSize: '1.25rem' }}>Find out More</Button>
          </Carousel.Caption>
        </Carousel.Item>
        <Carousel.Item>
          <img className="d-block w-100" src="img_1.jpg" alt="First slide" />
          <Carousel.Caption className="carousel-caption">
            <h2>Medical Waste Management</h2>
            <p className="feature-card-p">We provide medical waste management services to a wide range of healthcare and related facilities.</p>
            <Button onClick={handleFindOutMore}
              variant="primary" type="button" style={{ display: 'flex', flexDirection: 'column', fontSize: '1.25rem' }}>Find out More</Button>
          </Carousel.Caption>
        </Carousel.Item>
        <Carousel.Item>
          <img className="d-block w-100" src="/autoclave.jpeg" alt="Autoclave" />
          <Carousel.Caption className="carousel-caption">
            <h2>Autoclave Sterilization & Pre-Treatment</h2>
            <p>Industrial autoclaving for sterilization, shredding and volume reduction prior to incineration.</p>
            <Button onClick={() => navigate('/services/autoclave')} variant="primary" type="button" style={{ display: 'flex', flexDirection: 'column', fontSize: '1.25rem' }}>Find out More</Button>
          </Carousel.Caption>
        </Carousel.Item>
        <Carousel.Item>
          <img className="d-block w-100" src="img_3.jpg" alt="Third slide" />
          <Carousel.Caption className="carousel-caption">
            <h2>Chemical Waste Management</h2>
            <p>We provide chemical waste management services to a wide range of healthcare and related facilities.</p>
            <Button onClick={handleOTC} variant="primary" type="button" style={{ display: 'flex', alignItems: 'flex-end', flexDirection: 'column', fontSize: '1.25rem' }}>Find out More</Button>
          </Carousel.Caption>
        </Carousel.Item>
        
      </Carousel>
      <div id="why-choose-us" className='red-background'>
        <animated.h2 style={slideInWhyChoose}>Why Choose Us?</animated.h2>
        <div className="features-section" ref={sectionRef}>
          <div className={`feature-card ${isVisible ? 'fade-in' : ''}`}>
            <img src="/why-customized.svg" alt="Customized Service Plan" />
            <h3>Customized Service Plans</h3>
            <p style={{ textAlign: 'justify' }}>We design collection and disposal plans tailored to your operations — frequency, material types, and documentation — so you only pay for the services you need.</p>
          </div>
          <div className={`feature-card ${isVisible ? 'fade-in' : ''}`}>
            <img src="/why-compliance.svg" alt="Full Regulatory Compliance" />
            <h3>Full Regulatory Compliance</h3>
            <p style={{ textAlign: 'justify' }}>Licensed transport, chain-of-custody tracking, and professionally prepared manifest and disposal certificates to meet local and national regulations.</p>
          </div>
          <div className={`feature-card ${isVisible ? 'fade-in' : ''}`}>
            <img src="/why-sustainable.svg" alt="Sustainable Solutions" />
            <h3>Sustainable Solutions</h3>
            <p style={{ textAlign: 'justify' }}>We prioritize recycling and waste recovery where possible and advise on greener alternatives to reduce your environmental footprint.</p>
          </div>
        </div>
      </div>

      <section id="insurance-section" className="insurance-section">
        <div className="container">
          <animated.h2 style={slideIn}>We are certified by the Environment Protection Agency, Government of Punjab, Pakistan.</animated.h2>
          <animated.div style={logosAnimation} className="logos">
            <img src="govt-logo-1.png" alt="GoP" className="insurance-logo" />
            <img src="govt-logo-2-new.png" alt="GovtOfPakistan" className="insurance-logo" />
            {/* <img src="cvs_caremark_logo.svg" alt="CVS CareMark" className="insurance-logo" />
            <img src="Express_Scripts_logo.png" alt="Express Scripts" className="insurance-logo" />
            <img src="aptum-logo.png" alt="Aptum" className="insurance-logo" /> */}
          </animated.div>
        </div>
      </section>

      <div className="store-front-pic">
        <img style={{ width: '100%' }} src={imageSrc} alt="Store Front" />
      </div>
      <Review/>
    </Container>
  );
};

export default Home;
